import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { featuredApp } from "@/data/apps";
import { Reveal, SectionLabel } from "@/components/common/Reveal";
import { StoreButtons } from "@/components/common/StoreButtons";

export const FeaturedApp = () => {
  if (!featuredApp) return null;
  const app = featuredApp;

  return (
    <section className="relative py-24 sm:py-32" data-testid="home-featured-app">
      <div className="shell">
        <SectionLabel>Featured app</SectionLabel>

        <div className="mt-8 grid gap-14 lg:grid-cols-[0.95fr_1.05fr] lg:gap-20">
          <div>
            <Reveal>
              <div className="flex items-center gap-5">
                <img
                  src={app.icon}
                  alt={`${app.name} icon`}
                  className="h-20 w-20 rounded-3xl object-cover"
                  style={{ border: "1px solid var(--line)" }}
                />
                <div>
                  <h2 className="font-display text-3xl font-black tracking-tight text-white sm:text-4xl">
                    {app.name}
                  </h2>
                  <p className="mt-1 font-mono-x text-xs uppercase tracking-[0.16em] text-gray-500">
                    {app.category}
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <p className="font-display mt-8 text-xl font-bold leading-snug text-white/90 sm:text-2xl">
                {app.tagline}
              </p>
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-gray-400 sm:text-base">
                {app.fullDescription}
              </p>
            </Reveal>

            <Reveal delay={0.14}>
              <ul className="mt-10 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                {app.features.slice(0, 6).map((f) => (
                  <li key={f.title} className="flex items-start gap-2.5 text-sm text-gray-300">
                    <Check
                      className="mt-0.5 h-4 w-4 shrink-0"
                      style={{ color: "var(--accent)" }}
                      aria-hidden
                    />
                    {f.title}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-10 space-y-5">
                <StoreButtons app={app} />
                <Link
                  to={`/apps/${app.slug}`}
                  data-testid="featured-app-detail-link"
                  className="link-underline inline-flex items-center gap-1.5 text-sm text-white"
                >
                  See everything in {app.name}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:gap-6">
            {app.screenshots.slice(0, 4).map((s, i) => (
              <motion.figure
                key={s.url}
                initial={{ opacity: 0, y: 46 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.95, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className={`overflow-hidden rounded-3xl ${
                  i % 2 === 0 ? "sm:mt-14" : ""
                }`}
                style={{ border: "1px solid var(--line)", background: "var(--surface)" }}
              >
                <img
                  src={s.url}
                  alt={s.caption}
                  loading="lazy"
                  className="block aspect-[9/16] w-full object-cover object-top"
                />
                <figcaption className="px-4 py-3 text-xs text-gray-500">{s.caption}</figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
